import type { TodoEintrag } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { Link, useSearchParams } from "react-router";
import { schluessel, useStruktur, useTodos } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { useDarf } from "../huelle/konto.ts";
import { useHinweis } from "../ui/Hinweise.tsx";
import "./seiten.css";

const datum = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" });

function adresse(t: TodoEintrag): string {
  return `/p/${t.pfad}${t.ziel.slot !== undefined ? `?slot=${t.ziel.slot}` : ""}`;
}

export function Todos() {
  const id = useId();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const darf = useDarf("notiz");
  const struktur = useStruktur();
  const [suche, setSuche] = useSearchParams();
  const filter = { plugin: suche.get("plugin") ?? undefined, workspace: suche.get("workspace") ?? undefined, autor: suche.get("autor") ?? undefined };
  const alle = useTodos({});
  const liste = useTodos(filter);

  const plugins = struktur.data?.plugins ?? [];
  const workspaces = plugins.find((p) => p.id === filter.plugin)?.workspaces ?? [];
  const autoren = [...new Set((alle.data ?? []).map((t) => t.autorName))].sort((a, b) => a.localeCompare(b, "de"));
  const gefiltert = !!(filter.plugin || filter.workspace || filter.autor);

  const setze = (feld: "plugin" | "workspace" | "autor", wert: string) => {
    const neu = new URLSearchParams(suche);
    if (wert) neu.set(feld, wert);
    else neu.delete(feld);
    if (feld === "plugin") neu.delete("workspace");
    setSuche(neu, { replace: true });
  };

  const erledigen = async (t: TodoEintrag) => {
    try {
      await api(`/api/notizen/${t.id}`, { methode: "PATCH", body: { erledigt: true } });
      await Promise.all([qc.invalidateQueries({ queryKey: ["todos"] }), qc.invalidateQueries({ queryKey: ["notizen"] }), qc.invalidateQueries({ queryKey: schluessel.struktur })]);
      hinweis({ text: `„${t.text.length > 40 ? `${t.text.slice(0, 40)} …` : t.text}“ ist erledigt.` });
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
    }
  };

  return (
    <main className="seite">
      <header className="seite-kopf">
        <h1>Offene To-dos</h1>
        <p className="leise">To-dos aus allen Plugins, Workspaces, UIs und Slots.</p>
      </header>
      <div className="filterleiste" role="group" aria-label="Filter">
        <div className="feld">
          <label htmlFor={`${id}-p`}>Plugin</label>
          <select id={`${id}-p`} className="eingabe" value={filter.plugin ?? ""} onChange={(e) => setze("plugin", e.target.value)}>
            <option value="">Alle</option>
            {plugins.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="feld">
          <label htmlFor={`${id}-w`}>Workspace</label>
          <select id={`${id}-w`} className="eingabe" value={filter.workspace ?? ""} disabled={!filter.plugin} onChange={(e) => setze("workspace", e.target.value)}>
            <option value="">Alle</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="feld">
          <label htmlFor={`${id}-a`}>Autor</label>
          <select id={`${id}-a`} className="eingabe" value={filter.autor ?? ""} onChange={(e) => setze("autor", e.target.value)}>
            <option value="">Alle</option>
            {autoren.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>
      {liste.isError && <p className="feld-fehler">{liste.error.message}</p>}
      {liste.data?.length === 0 && <p className="seite-leer">{gefiltert ? "Keine offenen To-dos für diese Auswahl." : "Keine offenen To-dos. Alles erledigt."}</p>}
      {!!liste.data?.length && (
        <ul className="todo-liste">
          {liste.data.map((t) => (
            <li key={t.id} className="todo">
              <input type="checkbox" className="notiz-haken" aria-label={`Erledigt: ${t.text}`} disabled={!darf} onChange={() => void erledigen(t)} />
              <div className="todo-rumpf">
                <p className="todo-text">{t.text}</p>
                <p className="todo-meta">
                  <Link to={adresse(t)} className="mono">
                    {t.pfad}
                    {t.ziel.slot !== undefined && ` · Slot ${t.ziel.slot}`}
                  </Link>
                  <span>
                    {" "}
                    · {t.autorName} · <time dateTime={t.erstelltAm}>{datum.format(new Date(t.erstelltAm))}</time>
                  </span>
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
